/* Decompilador .ssc -> pseudo-.sst (port 1:1 de tools/decompilar_ssc_v2.py)
 *
 * SabreScribe / Sabre Scribe DOS 1989. Reversea el bytecode .ssc casi por completo
 * (ver tools/FORMATO_SSC.md). ES module sin dependencias, apto navegador y node.
 *
 * export decompileSsc(arrayBufferOrUint8Array) ->
 *   { ok:true, sst:'<pseudo-sst>', stats:{defines,dropdowns,comandos,windows,ifs,gotos} }
 *   { ok:false, error:'<motivo>' }
 *
 * EQUIVALENCIA con la salida stdout de decompilar_ssc_v2.py, byte a byte, EXCEPTO:
 *  (1) la 2da linea de cabecera dice "reconstruido por sscDecompiler.js" en vez de
 *      "reconstruido por decompilar_ssc_v2.py desde <path>" (no hay path en el browser).
 *  El bloque final ";=== COBERTURA ===" SE INCLUYE, identico al Python.
 *  `sst` termina en '\n' final, igual que el stdout del Python (text + '\n').
 *  Texto decodificado como cp1252 con semantica 'replace' de Python (los 5 bytes
 *  indefinidos 0x81/0x8D/0x8F/0x90/0x9D -> U+FFFD).
 *  Formateo de floats: replica str()/repr() de Python; en magnitudes extremas
 *  (>=1e16 no enteras representables, <1e-4) es best-effort (no ocurre en .ssc reales;
 *  C65I solo usa enteros como 20260505).
 */

const TYPES = { 0x80: 'ANY', 0x01: 'NUM', 0x02: 'DECIMAL', 0x04: 'ALPHA', 0x08: 'DATE', 0x10: 'TIME' };
const MAND_BIT = 0x100;
// Centinela "sin valor por defecto" para campos numericos (NaN-like de SabreScribe)
const NUM_EMPTY = [0x7d, 0x1d, 0x6e, 0x7e, 0x8f, 0x8f, 0x3e];
const CMP = { 0x12: '=', 0x79: '<>', 0x11: '>', 0x10: '<', 0x13: '>=', 0x0f: '<=' };

// ---------------------------------------------------------------- texto cp1252

// Tabla 0x80..0x9F de windows-1252 (resto: identidad con el codepoint).
// Los 5 huecos (0x81,0x8D,0x8F,0x90,0x9D) van a U+FFFD como cp1252+'replace' de Python.
const CP1252_HIGH = [
  0x20ac, 0xfffd, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021,
  0x02c6, 0x2030, 0x0160, 0x2039, 0x0152, 0xfffd, 0x017d, 0xfffd,
  0xfffd, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022, 0x2013, 0x2014,
  0x02dc, 0x2122, 0x0161, 0x203a, 0x0153, 0xfffd, 0x017e, 0x0178,
];
let _td = null;
try { _td = new TextDecoder('windows-1252'); } catch (e) { _td = null; }
const RX_CP1252_UNDEF = /[\u0081\u008D\u008F\u0090\u009D]/g;

function decodeCp1252(bytes) {
  if (_td) {
    // WHATWG mapea los 5 bytes indefinidos a controles C1; Python 'replace' da U+FFFD
    return _td.decode(bytes).replace(RX_CP1252_UNDEF, '\uFFFD').replace(/\u0001/g, '¤');
  }
  let r = '';
  for (let k = 0; k < bytes.length; k++) {
    const c = bytes[k];
    r += String.fromCharCode(c >= 0x80 && c <= 0x9f ? CP1252_HIGH[c - 0x80] : c);
  }
  return r.replace(/\u0001/g, '¤');
}

// ---------------------------------------------------------------- helpers Python

function hex2u(v) { return v.toString(16).toUpperCase().padStart(2, '0'); }   // %02X
function hex2l(v) { return v.toString(16).toLowerCase().padStart(2, '0'); }   // %02x
function hex4l(v) { return v.toString(16).toLowerCase().padStart(4, '0'); }   // %04x

function pyStrip(s, chars) {  // str.strip(chars)
  let a = 0, b = s.length;
  while (a < b && chars.indexOf(s[a]) !== -1) a++;
  while (b > a && chars.indexOf(s[b - 1]) !== -1) b--;
  return s.slice(a, b);
}

function pyRstrip(s, chars) {  // str.rstrip(chars)
  let b = s.length;
  while (b > 0 && chars.indexOf(s[b - 1]) !== -1) b--;
  return s.slice(0, b);
}

function pyIntStr(v) {  // str(int(v)) para v float entero (exacto via BigInt)
  if (Object.is(v, -0)) return '0';
  return BigInt(v).toString();
}

function pyInt(v) {  // int(v): trunca; lanza en NaN/inf como Python
  if (!Number.isFinite(v)) throw new RangeError('cannot convert float ' + v + ' to integer');
  return Math.trunc(v);
}

function fixExpStr(s) {  // notacion exponencial estilo Python: e+NN / e-NN (min 2 digitos)
  return s.replace(/e([+-])(\d)$/, 'e$10$2').replace(/e(\d)/, 'e+$1');
}

function pyFloatStr(v) {  // str(float) == repr(float) en Python 3
  if (Number.isNaN(v)) return 'nan';
  if (v === Infinity) return 'inf';
  if (v === -Infinity) return '-inf';
  if (Number.isInteger(v)) {
    if (Math.abs(v) < 1e16) {
      return (Object.is(v, -0) ? '-0' : BigInt(v).toString()) + '.0';
    }
    return fixExpStr(v.toExponential());  // Python pasa a exponencial en >=1e16
  }
  if (v !== 0 && Math.abs(v) < 1e-4) return fixExpStr(v.toExponential());  // Python: <1e-4
  const s = String(v);
  return s.indexOf('e') !== -1 ? fixExpStr(s) : s;
}

// ---------------------------------------------------------------- parsing

function parseVars(data, start) {
  // Tabla de variables: [reclen:u16le][flags:u16le][maxlen:u8][nombre\0][blob].
  // flags: byte bajo = tipo; bit 0x100 = MAND. Para en cuanto deja de parecer una var.
  let pos = start;
  const out = [];
  const n = data.length;
  while (pos + 6 < n) {
    const reclen = data[pos] | (data[pos + 1] << 8);
    const flags = data[pos + 2] | (data[pos + 3] << 8);
    const tb = flags & 0xff;
    if (reclen < 4 || reclen > 240 || TYPES[tb] === undefined) break;
    const maxlen = data[pos + 4];
    let i = pos + 5;
    const name = [];
    while (i < n && data[i] !== 0 && name.length < 80) { name.push(data[i]); i++; }
    if (!name.length) break;
    const recEnd = pos + 2 + reclen;
    const blob = data.subarray(Math.min(i + 1, n), Math.max(Math.min(recEnd, n), 0));  // tras el \0 del nombre
    out.push({
      name: decodeCp1252(Uint8Array.from(name)),
      type: TYPES[tb] !== undefined ? TYPES[tb] : '0x' + hex2l(tb),
      maxlen: maxlen,
      mand: !!(flags & MAND_BIT),
      blob: blob,
      offset: pos,
    });
    pos = recEnd;
  }
  return [out, pos];
}

function parsePool(data, start, codeOff) {
  // Pool de literales tras la tabla de vars y hasta el codigo.
  // Mezcla strings [len:u16le] <payload> (payload = 00 + texto + 00) con
  // registros de @-variable (botones / system vars) con la MISMA forma que una var.
  const pool = [];  // cada item: {kind:'STR'|'VAR', text, offset, ...}
  let pos = start;
  const n = data.length;
  while (pos < codeOff && pos + 4 < n) {
    const reclen = data[pos] | (data[pos + 1] << 8);
    const flags = data[pos + 2] | (data[pos + 3] << 8);
    const tb = flags & 0xff;
    const recEnd = pos + 2 + reclen;
    if (TYPES[tb] !== undefined && reclen < 240 && data[pos + 4] < 0x40) {
      // registro tipo variable (@BTN.., @SYSTEM_ERROR, ...)
      let i = pos + 5;
      const name = [];
      while (i < n && data[i] !== 0) { name.push(data[i]); i++; }
      pool.push({
        kind: 'VAR', text: decodeCp1252(Uint8Array.from(name)),
        type: TYPES[tb] !== undefined ? TYPES[tb] : '0x' + hex2l(tb), maxlen: data[pos + 4],
        mand: !!(flags & MAND_BIT), offset: pos,
      });
    } else {
      const payload = data.subarray(Math.min(pos + 2, n), Math.max(Math.min(recEnd, n), 0));
      let s = 0, e = payload.length;                       // strip(b'\x00')
      while (s < e && payload[s] === 0) s++;
      while (e > s && payload[e - 1] === 0) e--;
      pool.push({ kind: 'STR', text: decodeCp1252(payload.subarray(s, e)), offset: pos });
    }
    pos = recEnd;
  }
  return [pool, pos];
}

function numDefault(blob) {
  // Devuelve el valor default numerico (f64 LE) o null si es el centinela 'vacio'.
  if (!blob || !blob.length) return null;
  const body = blob[blob.length - 1] === 0 ? blob.subarray(0, blob.length - 1) : blob;
  if (!body.length) return null;
  if (body.length >= 7) {
    let isEmpty = true;
    for (let k = 0; k < 7; k++) if (body[k] !== NUM_EMPTY[k]) { isEmpty = false; break; }
    if (isEmpty) return null;
  }
  if (body.length >= 8) {
    try {
      const dv = new DataView(body.buffer, body.byteOffset, body.byteLength);
      const v = dv.getFloat64(0, true);
      if (!Number.isFinite(v)) return null;           // int(v) lanzaria en Python
      if (Number.isInteger(v)) return pyIntStr(v);    // str(int(v))
      return pyFloatStr(v);                           // repr(v)
    } catch (e) { return null; }
  }
  return null;
}

// ---------------------------------------------------------------- symbol table

function buildSymbols(vars, pool) {
  // Tabla combinada indexada 0-based: vars[0..V-1] luego pool[V..].
  // El codigo referencia los simbolos con indice 1-based (operando = idx0 + 1).
  const sym = [];
  for (const v of vars) {
    sym.push({ k: 'VAR', name: v.name, is_num: v.type === 'NUM' || v.type === 'DECIMAL' });
  }
  for (const p of pool) {
    if (p.kind === 'VAR') sym.push({ k: 'BTN', name: p.text, is_num: false });
    else sym.push({ k: 'STR', name: p.text, is_num: false });
  }
  return sym;
}

function optClean(text) {
  // ' SELECCIONAR=SEL|' -> 'SELECCIONAR=SEL' (saca espacio inicial cosmetico y | final)
  let t = text;
  if (t.startsWith(' ')) t = t.slice(1);
  return pyRstrip(t, '|');
}

function strClean(text) {
  // Literal de comando/ventana: saca el espacio inicial cosmetico
  return text.startsWith(' ') ? text.slice(1) : text;
}

// ---------------------------------------------------------------- dropdowns (PART1)

function parseDropdownTable(data, p2start, codeOff) {
  // PART1 del area de codigo: define los dropdowns.
  //   69 47 <opt1>   primera opcion (1-based)
  //   65 47 <optN>   opciones siguientes
  //   69 94 <var>    variable #D duena del grupo
  // Devuelve [Map{var_idx0 -> [opt_idx0,...]}, offset de la parte ejecutable]
  const groups = new Map();
  let cur = [];
  let i = p2start;
  const n = data.length;
  while (i + 3 < n && i < codeOff + 2000) {
    const a = data[i], b = data[i + 1], c = data[i + 2], d = data[i + 3];
    if ((a === 0x69 || a === 0x65) && b === 0x47 && d === 0) {        // opcion
      cur.push(c - 1);
      i += 4;
    } else if (a === 0x69 && b === 0x94 && d === 0) {                 // var #D del grupo
      groups.set(c - 1, cur);
      cur = [];
      i += 4;
    } else if ((a === 0x69 || a === 0x65) && b === 0x74 && d === 0) { // label de boton (transicion)
      i += 4;
    } else {
      break;
    }
  }
  return [groups, i];
}

// ---------------------------------------------------------------- code (PART2)

function disasmCode(data, start, sym) {
  // Reconstruye la parte ejecutable a pseudo-Scribe. Devuelve [lineas, info].
  const n = data.length;
  const dv = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const getF64 = (off) => dv.getFloat64(off, true);  // lanza RangeError como struct.error
  const out = [];
  let i = start;
  const host = [];           // buffer del comando host en curso (>>...<<)
  let lastRefs = [];         // ultimos simbolos 'empujados' (para IF a op b)
  const endifTargets = [];   // multiset de offsets donde cerrar IF (orden descendente)
  const codeOff = data[3] | (data[4] << 8);
  const labelsUsed = [];
  let indent = 0;

  const ind = () => '  '.repeat(Math.max(0, indent));

  const flushHost = () => {
    if (host.length) {
      out.push(ind() + '\u00bb' + host.join('') + '\u00ab');
      host.length = 0;
    }
  };

  const symname = (idx0) => {
    if (typeof idx0 !== 'number') throw new TypeError("'<=' not supported");  // como Python con tuple
    if (idx0 >= 0 && idx0 < sym.length) {
      const s = sym[idx0];
      if (s.k === 'VAR' || s.k === 'BTN') return '[' + s.name + ']';
      return strClean(s.name);
    }
    return '[?sym' + idx0 + ']';
  };

  const symraw = (idx0) => (idx0 >= 0 && idx0 < sym.length) ? sym[idx0] : { k: 'STR', name: '?', is_num: false };

  while (i < n) {
    // cerrar IFs cuyo destino sea esta posicion
    while (endifTargets.length && endifTargets[endifTargets.length - 1] <= i) {
      endifTargets.pop();
      indent -= 1;
      out.push(ind() + 'ENDIF');
    }

    const b = data[i];

    // ----- push de simbolo:  [69|65|6f][47|07|90|94|74][idx1based]
    if ((b === 0x69 || b === 0x65 || b === 0x6f) && i + 2 < n &&
        (data[i + 1] === 0x47 || data[i + 1] === 0x07 || data[i + 1] === 0x90 ||
         data[i + 1] === 0x94 || data[i + 1] === 0x74)) {
      const t = data[i + 1];
      const idx0 = data[i + 2] - 1;
      let adv = 3;
      if (i + 3 < n && data[i + 3] === 0) adv = 4;
      if (t === 0x90) {
        // asignacion / set-value (DEFAULT [V]=[V])
        if (lastRefs.length) {
          const tgt = lastRefs[lastRefs.length - 1];
          out.push(ind() + 'DEFAULT ' + symname(idx0) + ' = ' + symname(tgt));
        }
        i += adv;
        continue;
      }
      lastRefs.push(idx0);
      if (lastRefs.length > 4) lastRefs.shift();
      i += adv;
      continue;
    }

    // ----- push float literal:  69 08 <f64 LE>
    if (b === 0x69 && i + 1 < n && data[i + 1] === 0x08 && i + 10 <= n) {
      const val = getF64(i + 2);
      lastRefs.push(['F', val]);
      i += 10;
      continue;
    }

    // ----- EMIT al host:  20 4f  (vuelca el ultimo push al buffer)
    if (b === 0x20 && i + 1 < n && data[i + 1] === 0x4f) {
      if (lastRefs.length) {
        const r = lastRefs.pop();
        if (Array.isArray(r)) host.push(pyFloatStr(r[1]));
        else host.push(symname(r));
      }
      i += 2;
      continue;
    }

    // ----- tecla especial:  1e XX 00
    if (b === 0x1e && i + 2 < n && data[i + 2] === 0) {
      const key = data[i + 1];
      const tok = key === 0x81 ? '{ENTER}' : key === 0x7c ? '{CLEAR}' : '{KEY_' + hex2u(key) + '}';
      host.push(tok);
      if (key === 0x81) flushHost();  // ENTER transmite -> cierra el comando host
      i += 3;
      continue;
    }

    // ----- READ EMUFIND:  2e ...
    if (b === 0x2e) {
      flushHost();
      let j = i + 1;
      let fstr = null, R = null, C = null, search = null, target = null;
      // F:  69 47 <idx> [00] 1d 4f
      if (j + 2 < n && (data[j] === 0x69 || data[j] === 0x65)) {
        fstr = symraw(data[j + 2] - 1);
        j += 3;
        if (j < n && data[j] === 0) j += 1;
      }
      if (j + 1 < n && data[j] === 0x1d && data[j + 1] === 0x4f) j += 2;
      // R:  69 08 <f64> 82 0f
      if (j + 1 < n && data[j] === 0x69 && data[j + 1] === 0x08) {
        R = getF64(j + 2); j += 10;
        if (j + 1 < n && data[j] === 0x82 && data[j + 1] === 0x0f) j += 2;
      }
      // C:  69 08 <f64> 83 0f
      if (j + 1 < n && data[j] === 0x69 && data[j + 1] === 0x08) {
        C = getF64(j + 2); j += 10;
        if (j + 1 < n && data[j] === 0x83 && data[j + 1] === 0x0f) j += 2;
      }
      // opcional search:  22 <stringref>   y luego target:  22 <varref>
      const stores = [];
      while (j + 1 < n && data[j] === 0x22) {
        stores.push(data[j + 1] - 1);
        j += 2;
        if (j < n && data[j] === 0) j += 1;
      }
      if (stores.length) {
        // el ultimo store que sea VAR = target; un store STR previo = search
        for (const s of stores) {
          const sr = symraw(s);
          if (sr.k === 'VAR' || sr.k === 'BTN') target = sr;
          else search = sr;
        }
      }
      const ftxt = fstr ? strClean(fstr.name) : 'EMUFIND:';
      const parts = ['READ F="' + ftxt + '"'];
      if (R !== null) parts.push('R=' + pyInt(R));
      if (C !== null) parts.push('C=' + pyInt(C));
      if (search !== null) parts.push('"' + strClean(search.name) + '",');
      if (target !== null) parts.push('[' + target.name + ']');
      out.push(ind() + parts.join(' '));
      lastRefs.length = 0;
      i = j;
      continue;
    }

    // ----- WINDOW:  <push title> 1b 4f  ...  13 (ENDWINDOW)
    if (b === 0x1b && i + 1 < n && data[i + 1] === 0x4f) {
      flushHost();
      let title = '';
      if (lastRefs.length) {
        const tr = lastRefs.pop();
        if (!Array.isArray(tr)) title = pyStrip(symname(tr), '[]');
      }
      out.push(ind() + 'WINDOW H="' + title + '"');
      indent += 1;
      let j = i + 2;
      const rows = new Map();   // row -> texto
      const placed = [];        // [row, col, ref] interpolaciones / botones
      while (j < n && data[j] !== 0x13) {
        if (data[j] === 0x21 && j + 3 < n) {            // 21 00 <row> <strref> 00
          const row = data[j + 2];
          const ref = data[j + 3] - 1;
          rows.set(row, strClean(symraw(ref).name));
          j += 4;
          if (j < n && data[j] === 0) j += 1;
        } else if (data[j] === 0x14 && j + 3 < n) {     // 14 <col> <row> <ref> 00
          const col = data[j + 1], row = data[j + 2], ref = data[j + 3] - 1;
          placed.push([row, col, ref]);
          j += 4;
          if (j < n && data[j] === 0) j += 1;
        } else {
          j += 1;
        }
      }
      // emitir cuerpo de la ventana por filas
      const allrows = [...new Set([...rows.keys(), ...placed.map((p) => p[0])])].sort((a, b) => a - b);
      for (const r of allrows) {
        const seg = [];
        if (rows.has(r)) seg.push(rows.get(r));
        const here = placed.filter((p) => p[0] === r).sort((a, b) => a[1] - b[1]);  // estable, por col
        for (const pl of here) seg.push(symname(pl[2]));
        out.push('  '.repeat(Math.max(0, indent)) + seg.join('   '));
      }
      indent -= 1;
      out.push(ind() + 'ENDWINDOW');
      lastRefs.length = 0;
      if (j < n && data[j] === 0x13) j += 1;
      i = j;
      continue;
    }

    // ----- comparacion + IF:  <cmp> <u16 destino-ENDIF>
    if (CMP[b] !== undefined && i + 2 < n) {
      flushHost();
      const target = codeOff + (data[i + 1] | (data[i + 2] << 8));
      // los dos ultimos refs son los operandos
      const ops = lastRefs.slice(-2);
      const fmt = (r) => {
        if (Array.isArray(r)) return pyFloatStr(r[1]);
        const sr = symraw(r);
        if (sr.k === 'STR') return '"' + strClean(sr.name) + '"';  // literal -> entre comillas
        return symname(r);
      };
      let cond;
      if (ops.length >= 2) cond = fmt(ops[0]) + ' ' + CMP[b] + ' ' + fmt(ops[1]);
      else if (ops.length === 1) cond = fmt(ops[0]) + ' ' + CMP[b] + ' ?';
      else cond = '? ' + CMP[b] + ' ?';
      out.push(ind() + 'IF ' + cond + ' THEN');
      indent += 1;
      endifTargets.push(target);
      endifTargets.sort((a, b) => b - a);
      lastRefs.length = 0;
      i += 3;
      continue;
    }

    // ----- GOTO:  0a <labelid> 03
    if (b === 0x0a && i + 1 < n) {
      const lid = data[i + 1];
      labelsUsed.push(lid);
      out.push(ind() + 'GOTO LBL_' + hex2u(lid) + '   ;[?] nombre original de la etiqueta no esta en el binario');
      i += 2;
      if (i < n && data[i] === 0x03) i += 1;
      continue;
    }

    // ----- EXIT / fin  (09 09 = EXIT + fin-de-programa -> un solo EXIT)
    if (b === 0x09) {
      flushHost();
      if (!(out.length && out[out.length - 1].trim() === 'EXIT')) {
        out.push(ind() + 'EXIT');
      }
      i += 1;
      continue;
    }

    // ----- desconocido
    if (b === 0x00) {
      i += 1;
      continue;
    }
    out.push(ind() + ';[?] opcode 0x' + hex2l(b) + ' @0x' + hex4l(i));
    i += 1;
  }

  flushHost();
  while (endifTargets.length) {
    endifTargets.pop();
    indent -= 1;
    out.push(ind() + 'ENDIF');
  }
  return [out, { labels: [...new Set(labelsUsed)].sort((a, b) => a - b) }];
}

// ---------------------------------------------------------------- emit pseudo-sst

function emit(data) {
  const codeOff = data[3] | (data[4] << 8);
  const [vars, tend] = parseVars(data, 7);
  const [pool] = parsePool(data, tend, codeOff);
  const sym = buildSymbols(vars, pool);
  // parte ejecutable empieza tras los 0-3 bytes residuales del ultimo blob
  let p2 = codeOff;
  if (data[p2] === 0x8f || data[p2] === 0x3e || data[p2] === 0x00) {
    while (p2 < data.length && data[p2] !== 0x69 && data[p2] !== 0x65) p2 += 1;
  }
  const [groups, execStart] = parseDropdownTable(data, p2, codeOff);
  let [codeLines, codeinfo] = disasmCode(data, execStart, sym);

  // --- resolver/colocar las etiquetas (GOTO LBL_xx).  Los nombres originales
  //     (REMARKS:, SALIR:) no estan en el binario; el id es lineal con el offset
  //     destino, asi que el id menor cae en el bloque previo al final y el mayor
  //     en EXIT. Insertamos marcadores LBL_xx: validos en esos puntos.
  const ids = codeinfo.labels;
  if (ids.length) {
    // sitio 1 = primera linea (indent 0) tras el ultimo ENDIF de nivel 0
    let lastEndif = -1;
    for (let k = 0; k < codeLines.length; k++) if (codeLines[k].trim() === 'ENDIF') lastEndif = k;
    let siteRemarks = null;
    for (let k = lastEndif + 1; k < codeLines.length; k++) {
      const s = codeLines[k];
      if (s.startsWith('\u00bb') || (s.trim() && !s.trimStart().startsWith('GOTO'))) {
        siteRemarks = k;
        break;
      }
    }
    let siteExit = null;
    for (let k = 0; k < codeLines.length; k++) {
      if (codeLines[k].trim() === 'EXIT') { siteExit = k; break; }
    }
    const sortedIds = [...ids].sort((a, b) => a - b);
    const marks = new Map();
    if (sortedIds.length >= 1 && siteRemarks !== null) marks.set(siteRemarks, 'LBL_' + hex2u(sortedIds[0]) + ':');
    if (sortedIds.length >= 2 && siteExit !== null) marks.set(siteExit, 'LBL_' + hex2u(sortedIds[sortedIds.length - 1]) + ':');
    const newlines = [];
    for (let k = 0; k < codeLines.length; k++) {
      if (marks.has(k)) newlines.push(marks.get(k));
      newlines.push(codeLines[k]);
    }
    codeLines = newlines;
  }

  const L = [];
  L.push(';==========================================================================');
  L.push(';  pseudo-.sst reconstruido por sscDecompiler.js (port de decompilar_ssc_v2.py)');
  L.push(';  (NOTA: las descripciones de DEFINE y los nombres de label NO existen en el .ssc)');
  L.push(';==========================================================================');
  L.push('');

  // --- DEFINEs
  const regular = vars.filter((v) => !v.name.endsWith('#C') && !v.name.endsWith('#D'));
  const cb = vars.filter((v) => v.name.endsWith('#C'));
  const dd = vars.filter((v) => v.name.endsWith('#D'));
  for (const v of regular) {
    const nd = (v.type === 'NUM' || v.type === 'DECIMAL') ? numDefault(v.blob) : null;
    const defv = nd ? nd : '';
    L.push('DEFINE [' + v.name + '=' + v.type + ':' + v.maxlen + ':' + (v.mand ? 'MAND' : 'OPT') + ':::' + defv + ']');
  }
  L.push('');
  for (const v of cb) L.push('DEFINE [' + v.name + '=' + v.type + '::OPT:::]');
  L.push('');
  for (const v of dd) L.push('DEFINE [' + v.name + '=' + v.type + '::OPT:::]');
  L.push('');

  // --- DEFAULTs (dropdowns), en el orden en que el codigo los liga
  for (const [varIdx0, optIdxs] of groups) {
    if (!(varIdx0 >= 0 && varIdx0 < sym.length)) continue;
    const vname = sym[varIdx0].name;
    const opts = [];
    for (const oi of optIdxs) {
      if (oi >= 0 && oi < sym.length) opts.push(optClean(sym[oi].name));
    }
    if (!opts.length) continue;
    // formato Scribe: "op1|"+ \n "op2|"+ ...
    L.push('DEFAULT [' + vname + ']="' + opts.join('|"+\n"') + '|"');
  }
  L.push('');

  // --- BOTONES
  const btns = pool.filter((p) => p.kind === 'VAR' && p.text.startsWith('@BTN_L'));
  if (btns.length) {
    L.push('BOTONES:');
    // buscar la etiqueta (string que sigue a cada boton en el pool)
    for (let bi = 0; bi < pool.length; bi++) {
      const p = pool[bi];
      if (p.kind === 'VAR' && p.text.startsWith('@BTN_L')) {
        let label = '';
        if (bi + 1 < pool.length && pool[bi + 1].kind === 'STR') {
          label = strClean(pool[bi + 1].text);
        }
        L.push('[' + p.text + ']= "' + label + '"');
      }
    }
    L.push('');
  }

  // --- CODIGO
  L.push('INICIAR:');
  L.push('');
  for (const x of codeLines) L.push(x);
  L.push('');

  // --- COBERTURA
  const trimmed = codeLines.map((x) => x.trim());
  L.push(';=== COBERTURA ===');
  L.push(';  variables (DEFINE)      : ' + vars.length);
  L.push(';     regulares            : ' + regular.length);
  L.push(';     checkbox  (#C)       : ' + cb.length);
  L.push(';     dropdown  (#D)       : ' + dd.length);
  L.push(';  dropdowns con opciones  : ' + [...groups.values()].filter((g) => g.length).length + ' (de ' + dd.length + ' #D)');
  L.push(';  opciones de dropdown    : ' + [...groups.values()].reduce((acc, g) => acc + g.length, 0));
  L.push(';  literales/strings pool  : ' + pool.filter((p) => p.kind === 'STR').length);
  L.push(';  botones/system-vars     : ' + pool.filter((p) => p.kind === 'VAR').length);
  L.push(';  lineas de codigo        : ' + trimmed.filter((x) => x).length);
  L.push(';  IF/THEN detectados      : ' + trimmed.filter((x) => x.startsWith('IF ')).length);
  L.push(';  ENDIF                   : ' + trimmed.filter((x) => x === 'ENDIF').length);
  L.push(';  WINDOW                  : ' + trimmed.filter((x) => x.startsWith('WINDOW')).length);
  L.push(';  READ EMUFIND            : ' + trimmed.filter((x) => x.startsWith('READ ')).length);
  L.push(';  comandos host >>..<<    : ' + trimmed.filter((x) => x.startsWith('\u00bb')).length);
  L.push(';  GOTO                    : ' + trimmed.filter((x) => x.startsWith('GOTO')).length +
         ' (labels ids: ' + codeinfo.labels.map((l) => '0x' + hex2u(l)).join(',') + ')');
  L.push(';  INCIERTO ;[?]           : ' + codeLines.filter((x) => x.indexOf(';[?]') !== -1 && x.indexOf('GOTO') === -1).length);

  return {
    text: L.join('\n'),
    info: { vars, pool, groups, sym, codeLines, ddCount: dd.length },
  };
}

// ---------------------------------------------------------------- API publica

export function decompileSsc(input) {
  try {
    let data = null;
    if (input instanceof Uint8Array) {
      data = input;
    } else if (typeof ArrayBuffer !== 'undefined' && input instanceof ArrayBuffer) {
      data = new Uint8Array(input);
    } else if (input && input.buffer && typeof input.byteLength === 'number') {
      data = new Uint8Array(input.buffer, input.byteOffset || 0, input.byteLength);
    } else {
      return { ok: false, error: 'entrada invalida: se espera ArrayBuffer o Uint8Array' };
    }
    if (data.length < 8) {
      return { ok: false, error: 'archivo demasiado corto para ser un .ssc (header de 7 bytes + datos)' };
    }
    if (data[0] !== 0x03) {
      return { ok: false, error: 'header invalido: byte0=0x' + hex2l(data[0]) + ' (se esperaba 0x03, no parece un .ssc)' };
    }
    const codeOff = data[3] | (data[4] << 8);
    if (codeOff < 7 || codeOff >= data.length) {
      return { ok: false, error: 'offset de codigo fuera de rango (0x' + codeOff.toString(16) + '), .ssc corrupto' };
    }
    const { text, info } = emit(data);
    if (!info.vars.length) {
      return { ok: false, error: 'tabla de variables ilegible (0 registros DEFINE), no parece un .ssc' };
    }
    const trimmed = info.codeLines.map((x) => x.trim());
    const stats = {
      defines: info.vars.length,
      dropdowns: info.ddCount,
      comandos: trimmed.filter((x) => x.startsWith('\u00bb')).length,
      windows: trimmed.filter((x) => x.startsWith('WINDOW')).length,
      ifs: trimmed.filter((x) => x.startsWith('IF ')).length,
      gotos: trimmed.filter((x) => x.startsWith('GOTO')).length,
    };
    return { ok: true, sst: text + '\n', stats };
  } catch (e) {
    return { ok: false, error: 'decompilacion fallida: ' + (e && e.message ? e.message : String(e)) };
  }
}
